/**
 * smStateEvents — a state's On Enter / On Exit events.
 *
 * A state node can carry `data.onEnter: string[]` and `data.onExit: string[]`,
 * the names of custom events to fire once when the state is entered and once
 * when it is left — Unreal's state-machine OnEnter / OnExit, expressed with the
 * same custom events a transition's Actions already fire. Both go through
 * runTransitionActions, so they reach EventRuntime through the one decoupled
 * seam App.jsx installs (GraphEvaluator must not import EventRuntime).
 *
 * WHEN they fire (decided with Florent, 2026-10-02):
 *   - On a transition, at the moment it is TAKEN — the start of the crossfade,
 *     not its end: old state's Exit, then the transition's Actions, then the
 *     new state's Enter. Same moment transition Actions already fire at.
 *   - On a SILENT DROP the old state's Exit still fires: the Entry edge was
 *     re-pointed, the active state was deleted, or the state machine left its
 *     graph (blueprint / character switch). A state that was entered is
 *     always exited, so an Enter that set something up has an Exit that can
 *     tear it down.
 *
 * The Exit list is CACHED at enter time (`runtime.exitActions`), because a
 * drop is exactly the case where the state node may no longer exist to read
 * it from. When the node still exists its live list wins, so editing On Exit
 * while the state is active is honoured.
 *
 * Not a per-frame path: these run only when a state actually changes.
 */
import { runTransitionActions } from './smTransitionActions.js'

/**
 * Record that `stateNode` is now the active state of `runtime` and fire its
 * On Enter events.
 * @param {object} runtime    smRuntime[smNodeId]
 * @param {string} smNodeId
 * @param {object|null} stateNode
 * @param {string|null} [from]  the state being left, or null on first entry
 */
export function enterState(runtime, smNodeId, stateNode, from = null) {
  runtime.exitStateId = stateNode?.id ?? null
  runtime.exitActions = stateNode?.data?.onExit ?? null
  const onEnter = stateNode?.data?.onEnter
  if (!onEnter || onEnter.length === 0) return
  runTransitionActions(onEnter, { smNodeId, phase: 'enter', state: stateNode.id, from })
}

/**
 * Fire the On Exit events of the state `runtime` last entered, once. Safe to
 * call when nothing was entered (no-op), and clears the record so a second
 * call cannot fire them again.
 * @param {object} runtime
 * @param {string} smNodeId
 * @param {object|null} liveStateNode  the state node if it still exists
 * @param {string|null} to       the state being entered, or null on a drop
 * @param {'transition'|'dropped'} reason
 */
export function exitState(runtime, smNodeId, liveStateNode, to, reason) {
  const state = runtime.exitStateId
  if (state == null) return
  const actions = liveStateNode ? liveStateNode.data?.onExit : runtime.exitActions
  runtime.exitStateId = null
  runtime.exitActions = null
  if (!actions || actions.length === 0) return
  runTransitionActions(actions, { smNodeId, phase: 'exit', state, to, reason })
}
